import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined } from "@ant-design/icons";
import { Button, Col, ConfigProvider, DatePicker, Descriptions, Dropdown, Modal, Row, Select, Space, Tag, message } from "antd";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { StatCard } from "../../../components/statistics/StatCard";
import ExportableFilterableTable from "../../../components/tables/ExportableFilterableTable";
import { COLORS } from "../../../components/ui/colors";
import axiosInstance from "../../../variables/api/axiosInstance";

const { RangePicker } = DatePicker;
const TRANSACTIONS_ENDPOINT = "/api/mobile/tontine/transactions";

const mapStatusForBackend = (status) => {
  const normalized = (status || "").toString().toUpperCase();
  switch (normalized) {
    case "PAID": case "SUCCESS": case "APPROVED": return "APPROVED";
    case "ERROR": case "FAILED": return "FAILED";
    case "PENDING": case "EXPIRED": case "CANCELLED": return normalized;
    default: return undefined;
  }
};

const resolveOperator = (record) => {
  const pm = (record.paymentMethod || "").toLowerCase();
  const nw = (record.network || "").toLowerCase();
  if (pm.includes("t-money") || nw.includes("tmoney")) return "T-Money";
  if (pm.includes("flooz") || nw.includes("flooz")) return "Flooz";
  return "-";
};

const statusTag = (status) => {
  const s = mapStatusForBackend(status) || (status || "").toString().toUpperCase();
  switch (s) {
    case "APPROVED": case "PAID": case "SUCCESS": return <Tag color="green">Payé</Tag>;
    case "FAILED": case "ERROR": return <Tag color="red">Échoué</Tag>;
    case "EXPIRED": case "CANCELLED": return <Tag color="volcano">Expiré</Tag>;
    case "PENDING": return <Tag color="gold">En attente</Tag>;
    default: return <Tag>{status || "-"}</Tag>;
  }
};

const fmt = (value) =>
  typeof value === "number"
    ? new Intl.NumberFormat("fr-FR").format(value)
    : value || "-";

const fmtDate = (value) =>
  value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-";

const TransactionsTontine = () => {
  const [loading, setLoading] = useState(false);
  const [datas, setDatas] = useState([]);
  const [dates, setDates] = useState([]);
  const [statusFilter, setStatusFilter] = useState();
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = (record) => {
    setDetail(null);
    setDetailLoading(true);
    axiosInstance
      .get(`/api/mobile/tontine/transactions/${record.id}`)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail({}))
      .finally(() => setDetailLoading(false));
  };

  const fetchDatas = () => {
    setLoading(true);
    axiosInstance
      .get(TRANSACTIONS_ENDPOINT, {
        params: {
          startDate: dates?.[0] ? dates[0].format("YYYY-MM-DD") : undefined,
          endDate: dates?.[1] ? dates[1].format("YYYY-MM-DD") : undefined,
          status: mapStatusForBackend(statusFilter),
        },
      })
      .then((res) => setDatas(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDatas(); }, []); // eslint-disable-line

  const exportHeaders = ["Réf. Opérateur", "Numéro Assuré", "Numéro Paiement", "Montant (FCFA)", "Opérateur", "Statut", "Créé le", "Créé par"];
  const exportRow = (t) => [
    t.paymentReference || "-",
    t.numeroAssure || "-",
    t.numeroPaiement || "-",
    t.montant || 0,
    resolveOperator(t),
    t.status || "-",
    fmtDate(t.createdAt),
    t.createdBy || "-",
  ];

  const exportToExcel = () => {
    try {
      const rows = enrichedData.map((t) =>
        exportHeaders.reduce((acc, h, i) => { acc[h] = exportRow(t)[i]; return acc; }, {})
      );
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Transactions");
      XLSX.writeFile(wb, "transactions-tontine.xlsx");
      message.success("Export Excel réussi");
    } catch (e) { message.error("Erreur export Excel"); }
  };

  const exportToCSV = () => {
    try {
      const header = exportHeaders.join(",");
      const rows = enrichedData.map((t) => exportRow(t).map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
      const blob = new Blob(["﻿" + [header, ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "transactions-tontine.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      message.success("Export CSV réussi");
    } catch (e) { message.error("Erreur export CSV"); }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(13);
      doc.text("Transactions Tontine", 14, 14);
      autoTable(doc, {
        startY: 20,
        head: [exportHeaders],
        body: enrichedData.map(exportRow),
        styles: { fontSize: 7 },
        headStyles: { fillColor: [36, 33, 102] },
      });
      doc.save("transactions-tontine.pdf");
      message.success("Export PDF réussi");
    } catch (e) { message.error("Erreur export PDF"); }
  };

  const exportMenuItems = [
    { key: "excel", label: "Excel (.xlsx)", icon: <FileExcelOutlined style={{ color: "#217346" }} />, onClick: exportToExcel },
    { key: "pdf",   label: "PDF",           icon: <FilePdfOutlined  style={{ color: "#e53935" }} />, onClick: exportToPDF },
    { key: "csv",   label: "CSV",           icon: <FileTextOutlined style={{ color: "#1677ff" }} />, onClick: exportToCSV },
  ];

  // Statistiques (sur toutes les données chargées, pas filtrées client)
  const stats = useMemo(() => {
    const approved = datas.filter((t) => mapStatusForBackend(t.status) === "APPROVED");
    const total = approved.reduce((s, t) => s + (t.montant || 0), 0);
    const tmoney = approved.filter((t) => resolveOperator(t) === "T-Money").reduce((s, t) => s + (t.montant || 0), 0);
    const flooz = approved.filter((t) => resolveOperator(t) === "Flooz").reduce((s, t) => s + (t.montant || 0), 0);
    return { total, tmoney, flooz, count: approved.length };
  }, [datas]);

  // Données enrichies (ajout champ opérateur résolu pour filtres/export)
  const enrichedData = useMemo(() =>
    datas.map((t) => ({ ...t, operateur: resolveOperator(t) })),
    [datas]
  );

  const columns = [
    {
      title: "Réf. Opérateur",
      dataIndex: "paymentReference",
      key: "paymentReference",
      searchable: true,
      render: (v) => v ? v : <span style={{ color: "#aaa" }}>En attente</span>,
      exportValue: (row) => row.paymentReference || "-",
    },
    {
      title: "Numéro Assuré",
      dataIndex: "numeroAssure",
      key: "numeroAssure",
      searchable: true,
      exportValue: (row) => row.numeroAssure || "-",
    },
    {
      title: "Numéro Paiement",
      dataIndex: "numeroPaiement",
      key: "numeroPaiement",
      searchable: true,
      exportValue: (row) => row.numeroPaiement || "-",
    },
    {
      title: "Montant (FCFA)",
      dataIndex: "montant",
      key: "montant",
      render: fmt,
      exportValue: (row) => row.montant || 0,
    },
    {
      title: "Opérateur",
      dataIndex: "operateur",
      key: "operateur",
      filterType: "select",
      filterOptions: [
        { label: "T-Money", value: "T-Money" },
        { label: "Flooz", value: "Flooz" },
      ],
      exportValue: (row) => row.operateur || "-",
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: statusTag,
      filterType: "select",
      filterOptions: [
        { label: "Payé", value: "APPROVED" },
        { label: "En attente", value: "PENDING" },
        { label: "Échoué", value: "FAILED" },
        { label: "Expiré", value: "EXPIRED" },
      ],
      exportValue: (row) => row.status || "-",
    },
    {
      title: "Créé le",
      dataIndex: "createdAt",
      key: "createdAt",
      render: fmtDate,
      exportValue: (row) => fmtDate(row.createdAt),
    },
    {
      title: "Créé par",
      dataIndex: "createdBy",
      key: "createdBy",
      searchable: true,
      exportValue: (row) => row.createdBy || "-",
    },
  ];

  const toolbarExtras = (
    <Space wrap>
      <RangePicker onChange={(value) => setDates(value)} />
      <Select
        allowClear
        placeholder="Statut"
        style={{ width: 160 }}
        options={[
          { value: "APPROVED", label: "Payé" },
          { value: "PENDING", label: "En attente" },
          { value: "FAILED", label: "Échoué" },
        ]}
        onChange={(v) => setStatusFilter(v)}
      />
      <Button type="primary" onClick={fetchDatas}>Filtrer</Button>
    </Space>
  );

  return (
    <ConfigProvider locale={frFR}>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <SideBar currentMenu={"CAISSE"} />
          <div className="layout-page">
            <NavBar />
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">

                {/* Cartes statistiques */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={8}>
                    <StatCard
                      title="Montant total encaissé"
                      value={`${fmt(Math.trunc(stats.total))} FCFA`}
                      subtitle={`${stats.count} transaction${stats.count !== 1 ? "s" : ""} approuvée${stats.count !== 1 ? "s" : ""}`}
                      icon="bx bx-wallet"
                      iconBg={COLORS.success}
                      gradient={COLORS.success}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <StatCard
                      title="T-Money"
                      value={`${fmt(Math.trunc(stats.tmoney))} FCFA`}
                      subtitle="Transactions approuvées"
                      icon="bx bx-mobile"
                      iconBg={COLORS.primary}
                      gradient={COLORS.primary}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <StatCard
                      title="Flooz"
                      value={`${fmt(Math.trunc(stats.flooz))} FCFA`}
                      subtitle="Transactions approuvées"
                      icon="bx bx-mobile-alt"
                      iconBg={COLORS.warning}
                      gradient={COLORS.warning}
                    />
                  </Col>
                </Row>

                {/* Table */}
                <div className="card">
                  <div className="card-body">
                    <h5 style={{ marginBottom: 16, fontWeight: "bold" }}>Liste des transactions</h5>
                    <ExportableFilterableTable
                      columns={columns}
                      dataSource={enrichedData}
                      loading={loading}
                      showExport={false}
                      searchPlaceholder="Rechercher (réf., assuré, téléphone...)"
                      toolbarExtras={toolbarExtras}
                      pagination={{ showSizeChanger: true, showTotal: (t) => `Total : ${t} transactions` }}
                      onRow={(record) => ({
                        onClick: () => openDetail(record),
                        style: { cursor: "pointer" },
                      })}
                    />
                  </div>
                </div>

              </div>
              <Footer />
            </div>
          </div>
        </div>
      </div>

      {/* Bouton flottant export — bas droite */}
      <Dropdown menu={{ items: exportMenuItems }} placement="topRight" trigger={["click"]}>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          size="large"
          style={{
            position: "fixed",
            bottom: 32,
            right: 32,
            zIndex: 1000,
            borderRadius: 24,
            boxShadow: "0 4px 16px rgba(0,0,0,0.22)",
            paddingLeft: 20,
            paddingRight: 20,
          }}
        >
          Exporter
        </Button>
      </Dropdown>

      {/* Modal détail */}
      <Modal
        open={detail !== null}
        onCancel={() => setDetail(null)}
        footer={null}
        title="Détail de la transaction"
        width={600}
      >
        {detailLoading && <p>Chargement...</p>}
        {detail && !detailLoading && (
          <>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Réf. Opérateur">
                {detail.paymentReference || <span style={{ color: "#aaa" }}>En attente</span>}
              </Descriptions.Item>
              <Descriptions.Item label="Opérateur">{detail.paymentMethod || detail.network || "-"}</Descriptions.Item>
              <Descriptions.Item label="Numéro Paiement">{detail.numeroPaiement || "-"}</Descriptions.Item>
              <Descriptions.Item label="Montant">
                {detail.montant ? `${fmt(detail.montant)} FCFA` : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">{statusTag(detail.status)}</Descriptions.Item>
              <Descriptions.Item label="Créé le">{fmtDate(detail.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="Déclaré par">{detail.declarePar || "-"}</Descriptions.Item>
            </Descriptions>
            {detail.personne && (
              <>
                <h6 style={{ marginTop: 16, marginBottom: 8, fontWeight: "bold" }}>Assuré</h6>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Nom">{detail.personne.nom || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Prénom">{detail.personne.prenom || "-"}</Descriptions.Item>
                  <Descriptions.Item label="N° Assuré (M)">{detail.personne.numeroAssureM || "-"}</Descriptions.Item>
                  <Descriptions.Item label="N° Assuré (P)">{detail.personne.numeroAssureP || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Email">{detail.personne.email || "-"}</Descriptions.Item>
                </Descriptions>
              </>
            )}
          </>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default TransactionsTontine;
